/**
 * Parses the `form_responses` payload returned by GET /jobs/data-capture/:jobId
 * into a per-form, per-section structure for display.
 *
 * The backend returns one row per `job_task_form` submission, each carrying its
 * own `form_name` and a `form_data` JSON blob whose sections carry a
 * `section_name`. Flattening those rows into a single list loses the form
 * boundaries and misattributes captured photos between forms (WRK-5765).
 */

/** A single question/answer pair, plus any photos captured against it. */
export interface ParsedFormAnswer {
  question: string;
  answer: string;
  /** Resolved image URLs from an `s3_image` control. Empty for other controls. */
  images: string[];
}

/** One section of a submitted form. */
export interface ParsedFormSection {
  /** `section_name` from the stored form_data. Empty when the form has none. */
  sectionName: string;
  answers: ParsedFormAnswer[];
}

/** One submitted form (one `job_task_form` row). */
export interface ParsedFormGroup {
  /** Stable key — the `job_task_form` row id, so repeat submissions stay distinct. */
  key: string;
  formName: string;
  submittedAt: string | null;
  sections: ParsedFormSection[];
}

/** The subset of the API row this parser reads. */
export interface FormResponseRow {
  id: number;
  form_name?: string | null;
  form_data?: unknown;
  created_time?: string | null;
}

const GENERIC_FORM_LABEL = 'Form';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function trimmedOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve the heading for a submitted form. The `form` table join is reliable in
 * practice, but fall back through the embedded form_data name to a generic label
 * so a panel is never unlabelled.
 */
function resolveFormName(row: FormResponseRow, formData: Record<string, unknown>): string {
  return trimmedOrNull(row.form_name) ?? trimmedOrNull(formData.name) ?? GENERIC_FORM_LABEL;
}

/**
 * Normalise the many shapes `form_data` takes in the wild:
 * - a form object with a `sections` array (what the mobile app submits)
 * - a bare array of sections
 * - a single section object carrying `controls`
 * - a flat `{ question, answer }` object (legacy)
 */
function normaliseSections(formData: Record<string, unknown>): unknown[] {
  if (Array.isArray(formData)) {
    return formData;
  }
  return Array.isArray(formData.sections) ? formData.sections : [formData];
}

/**
 * Turn one control into an answer row, or null when it carries nothing to show.
 */
function parseControl(control: Record<string, unknown>): ParsedFormAnswer | null {
  const question = String(control.control_name ?? '');
  const controlType = String(control.control_type ?? '');
  const value = control.value;

  // Label-only controls are presentational headings, not answers.
  if (controlType === 'label') {
    return null;
  }

  if (controlType === 's3_image' && Array.isArray(value)) {
    const images: string[] = [];
    for (const entry of value) {
      const img = asRecord(entry);
      if (img && img.path != null) {
        images.push(String(img.path));
      }
    }
    return {
      question,
      answer: `${images.length} photo${images.length === 1 ? '' : 's'} captured`,
      images,
    };
  }

  if (controlType === 'map' && Array.isArray(value)) {
    const coord = asRecord(value[0]);
    if (!coord) {
      return null;
    }
    return {
      question,
      answer: `${Number(coord.latitude ?? 0).toFixed(6)}, ${Number(coord.longitude ?? 0).toFixed(6)}`,
      images: [],
    };
  }

  if (Array.isArray(value)) {
    return {
      question,
      answer: value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', '),
      images: [],
    };
  }

  if (value != null) {
    return { question, answer: String(value), images: [] };
  }

  return null;
}

function parseSection(section: unknown): ParsedFormSection | null {
  const sec = asRecord(section);
  if (!sec) {
    return null;
  }

  const sectionName = trimmedOrNull(sec.section_name) ?? '';
  const controls = sec.controls;

  if (Array.isArray(controls)) {
    const answers: ParsedFormAnswer[] = [];
    for (const control of controls) {
      const ctrl = asRecord(control);
      if (!ctrl) {
        continue;
      }
      const answer = parseControl(ctrl);
      if (answer) {
        answers.push(answer);
      }
    }
    return { sectionName, answers };
  }

  // Legacy flat shape: a single { question, answer } object.
  if ('question' in sec) {
    return {
      sectionName,
      answers: [
        { question: String(sec.question ?? ''), answer: String(sec.answer ?? ''), images: [] },
      ],
    };
  }

  return null;
}

/**
 * Group the API's flat `form_responses` array into one entry per submitted form,
 * ordered by submission time (the order the crew actually completed them in).
 * Rows whose `form_data` is absent or non-object are skipped — the DB holds many
 * such placeholder rows for forms that were assigned but never filled in.
 */
export function parseFormResponses(rows: readonly FormResponseRow[]): ParsedFormGroup[] {
  const groups: ParsedFormGroup[] = [];

  for (const row of rows) {
    const formData = asRecord(row.form_data);
    if (!formData) {
      continue;
    }

    const sections: ParsedFormSection[] = [];
    for (const section of normaliseSections(formData)) {
      const parsed = parseSection(section);
      if (parsed) {
        sections.push(parsed);
      }
    }

    groups.push({
      key: String(row.id),
      formName: resolveFormName(row, formData),
      submittedAt: row.created_time ?? null,
      sections,
    });
  }

  // Ascending by submission time; rows without a timestamp keep their original
  // relative order at the end. Index tiebreak keeps the sort stable.
  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      const aTime = a.group.submittedAt ? Date.parse(a.group.submittedAt) : Number.NaN;
      const bTime = b.group.submittedAt ? Date.parse(b.group.submittedAt) : Number.NaN;
      const aValid = !Number.isNaN(aTime);
      const bValid = !Number.isNaN(bTime);

      if (aValid && bValid && aTime !== bTime) {
        return aTime - bTime;
      }
      if (aValid !== bValid) {
        return aValid ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map(({ group }) => group);
}

/**
 * Total answer count across a form's sections — used to decide whether a panel
 * has anything worth showing.
 */
export function countAnswers(group: ParsedFormGroup): number {
  return group.sections.reduce((total, section) => total + section.answers.length, 0);
}
